/**
 * Revit-style hover drag control for BIM walls.
 *
 * Hovering a wall with the Select tool shows a circular control on the
 * wall's location line — selection-free, like Revit's wall shape handles.
 * The control relocates between three stations following the cursor along
 * the run: start end / midpoint / end end (Revit's mid-left-right
 * relocation; hovering inside the outer 20% of the run pins it to that
 * end). Dragging:
 *  - a start/end station extends or shortens the wall through the FULL
 *    drafting snap pipeline (grid / polar / OSNAP / ortho) — the endpoint
 *    snaps onto the wall's OWN axis line ("the path it continues along",
 *    pure length change) and onto other walls' defining endpoints
 *    (junction OSNAP) within tolerance, but is NOT locked straight: off
 *    the locks the drag re-angles the wall freely (mm-rounded);
 *  - the midpoint station slides the whole wall along its axis.
 *
 * When the grabbed end is welded to other walls (a shared junction of two
 * chain walls) the control turns orange and the drag carries EVERY welded
 * object with the same delta (`applyJunctionStretch`), so extending at the
 * junction extends both walls; joint profiles re-solve after the commit.
 *
 * LIVE PREVIEW + commit mirror the selection shape handles exactly: drag
 * frames rebuild the in-progress geometryData from a pristine snapshot
 * through the shared pure builders (`wallHandlePreview`) and publish it to
 * `wallEditPreviewStore` (rAF-coalesced); on release the final target
 * rebuilds through the SAME builder and commits ONCE through the kernel.
 *
 * HOVER IS SELF-SUFFICIENT: the layer raycasts the scene itself (rAF-
 * coalesced, 2px threshold) instead of relying on the host tool's hover
 * state, so the same affordance works in every viewport that mounts it —
 * BIM plan/3D, the CAD/3D workbench and the Sketch workbench (walls render
 * there through the shared BIMElementLayer).
 *
 * Hidden while any tool other than SELECT is active; with
 * `hideOnSingleSelection`, also in 3D when the hovered wall is the single
 * selection (the BIM selection grips own it there).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '../state/store';
import { useBIMStore } from '../state/useBIMStore';
import { useWallEditPreviewStore } from '../state/wallEditPreviewStore';
import { WallElement, WallGeometryData } from '../core/types';
import { applyJunctionStretch, JUNCTION_WELD_TOL } from '../utils/JunctionMoveUtils';
import { GROUND_PLANE } from '../core/scene';
import {
    AxisGuideState,
    HANDLE_COLOR,
    HANDLE_SIZE,
    SNAP_COLOR,
    WallStation,
    buildPreviewGeometry,
    buildSlideGeometry,
    dragSnapTolerance,
    recomputeWallJoins,
    resolveEndpointDragTarget,
    resolveWallStation,
} from '../utils/wallHandlePreview';
import { WallAxisGuide } from './WallHandleGuides';

/** Plan-view control ring (world meters — reads like Revit's plan control). */
const RING_RADIUS = 0.13;
const RING_TUBE = 0.022;
const RING_Z = 0.05;

/** Module-level scratch for the pointer ray (device-rate event path). */
const _ndc = new THREE.Vector2();
const _hit = new THREE.Vector3();

/** Drag gesture state (one live drag at a time). */
interface HoverDragState {
    /** Resolved station at grab time (drives the drag semantics). */
    kind: WallStation;
    wallId: string;
    origGeom: WallGeometryData;
    /** Axis frame: `origin` = projection anchor, `dir` = unit run direction. */
    origin: { x: number; y: number };
    dir: { x: number; y: number };
    /** Mid-slide: axis projection of the pointer at drag start. */
    t0: number;
    /** Extend: the endpoint being dragged (its original plan position). */
    originEndpoint: [number, number];
    raf: number | null;
    lastEvent: PointerEvent | null;
}

/** Pointer drag target resolved by the active station. */
interface HoverDragTarget {
    /** End stations: the mm-rounded new endpoint (snapped or free). */
    endpoint?: [number, number, number];
    /** True when the endpoint locked onto the own axis or a junction endpoint. */
    locked?: boolean;
    /** Mid station: the mm-rounded slide delta along the axis. */
    slide?: { x: number; y: number };
}

export function WallHoverControlLayer({ hideOnSingleSelection = false }: { hideOnSingleSelection?: boolean }) {
    const tool = useAppStore((s) => s.activeTool);
    const viewType = useAppStore((s) => s.view.viewType);
    const isViewCubeHovered = useAppStore((s) => s.isViewCubeHovered);
    const appSelection = useAppStore((s) => s.selectedIds);
    const bimSelection = useBIMStore((s) => s.selection);
    const { camera, gl, raycaster, invalidate, scene } = useThree();

    // Self-sufficient hover: the layer finds the wall under the cursor with
    // its own scene raycast (see the idle effect below) — the control works
    // in every viewport that mounts it, not only where a host tool publishes
    // a hoveredId.
    const [hoverWallId, setHoverWallId] = useState<string | null>(null);
    // The wall the control is attached to: the hovered element while idle,
    // frozen to the dragged wall for the whole drag (the pointer may leave
    // the wall mid-drag without retargeting the gesture).
    const [dragWallId, setDragWallId] = useState<string | null>(null);
    const displayWallId = dragWallId ?? hoverWallId;
    const displayWall = useBIMStore(useShallow((s) => {
        if (!displayWallId) return null;
        const el = s.project.elements.get(displayWallId);
        return el && el.category === 'Walls' ? (el as WallElement) : null;
    }));

    // In 3D the single selection's own grips (WallShapeHandleLayer) serve the
    // wall; the hover control would duplicate them exactly.
    const singleSelectedId = useMemo(() => {
        const ids = new Set<string>([...appSelection, ...bimSelection]);
        return ids.size === 1 ? [...ids][0] : null;
    }, [appSelection, bimSelection]);

    const dragging = dragWallId != null;
    const active = tool === 'select' && (viewType === 'plan' || viewType === '3d');
    const enabled = active
        && !isViewCubeHovered
        && (dragging || !!displayWall)
        && !(hideOnSingleSelection && viewType === '3d' && !dragging && singleSelectedId === displayWall?.id);

    // The dragged wall's in-progress geometry (null unless a drag runs on
    // it): the control tracks the live preview instead of the committed data.
    const previewGeometry = useWallEditPreviewStore((s) =>
        displayWall && s.wallId === displayWall.id ? s.geometryData : null
    );
    const g = previewGeometry ?? displayWall?.geometryData;

    const [station, setStation] = useState<WallStation>('mid');
    const [axisGuide, setAxisGuide] = useState<AxisGuideState | null>(null);

    // Refs mirror the live values for the device-rate listeners (no
    // re-subscription per render, no stale closures in drag frames).
    const wallRef = useRef<WallElement | null>(null);
    wallRef.current = displayWall;
    const stationRef = useRef<WallStation>('mid');
    stationRef.current = station;
    const viewRef = useRef(viewType);
    viewRef.current = viewType;
    const dragRef = useRef<HoverDragState | null>(null);

    const applyStation = (next: WallStation) => {
        if (stationRef.current === next) return;
        stationRef.current = next;
        setStation(next);
        invalidate();
    };

    // A new wall under the cursor restarts at the midpoint station.
    useEffect(() => {
        applyStation('mid');
    }, [displayWallId]);

    useEffect(() => () => {
        // Unmount mid-drag (tool switch, hover change): drop the preview.
        const d = dragRef.current;
        if (d?.raf != null) cancelAnimationFrame(d.raf);
        dragRef.current = null;
        useWallEditPreviewStore.getState().clear();
    }, []);

    // Idle hover + station tracking: rAF-coalesced pointer moves raycast the
    // scene for the wall under the cursor (2px threshold — full-scene
    // raycasts on every mousemove are expensive on big models) and relocate
    // the control along the run. The 3D station reads the wall's vertical
    // mid-plane so it follows where the cursor points ON the wall; plan
    // views project onto the ground (exact top-down).
    useEffect(() => {
        if (!active) return;
        let raf: number | null = null;
        let last: PointerEvent | null = null;
        let lastClient: { x: number; y: number } | null = null;

        /** First scene hit that resolves to a BIM element id (instanced bulk
         *  or userData.id walk); helpers/grid/guides are transparent. */
        const resolveTopId = (hit: THREE.Intersection): string | null => {
            const instanceIds = hit.object.userData?.bimInstanceIds as string[] | undefined;
            if (instanceIds && hit.instanceId != null) return instanceIds[hit.instanceId] ?? null;
            let current: THREE.Object3D | null = hit.object;
            while (current) {
                const id = current.userData?.id as string | undefined;
                if (id) return id;
                current = current.parent;
            }
            return null;
        };

        const findHoverWallId = (): string | null => {
            const hits = raycaster.intersectObjects(scene.children, true);
            for (const hit of hits) {
                if (!hit.object.visible
                    || hit.object.userData?.isEventPlane === true
                    || hit.object.userData?.isWallHandle === true) continue;
                const id = resolveTopId(hit);
                if (!id) continue;
                const el = useBIMStore.getState().project.elements.get(id);
                // The first real occluder decides: a wall shows the control,
                // any other BIM element (door in front of a wall) hides it.
                return el && el.category === 'Walls' ? id : null;
            }
            return null;
        };

        const apply = () => {
            raf = null;
            const ev = last;
            last = null;
            if (!ev || dragRef.current) return;
            const rect = gl.domElement.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return;
            if (ev.clientX < rect.left || ev.clientX > rect.right || ev.clientY < rect.top || ev.clientY > rect.bottom) {
                setHoverWallId(null);
                return;
            }
            _ndc.set(
                ((ev.clientX - rect.left) / rect.width) * 2 - 1,
                -((ev.clientY - rect.top) / rect.height) * 2 + 1
            );
            camera.updateMatrixWorld();
            raycaster.setFromCamera(_ndc, camera);
            const id = findHoverWallId();
            setHoverWallId(id);
            if (!id) return;
            const wall = useBIMStore.getState().project.elements.get(id) as WallElement | undefined;
            if (!wall) return;
            const geom = wall.geometryData;
            let hit: THREE.Vector3 | null = null;
            if (viewRef.current === 'plan') {
                hit = raycaster.ray.intersectPlane(GROUND_PLANE, _hit);
            } else {
                const dx = geom.end[0] - geom.start[0];
                const dy = geom.end[1] - geom.start[1];
                const len = Math.hypot(dx, dy) || 1;
                const normal = new THREE.Vector3(dy / len, -dx / len, 0);
                const mid = new THREE.Vector3(
                    (geom.start[0] + geom.end[0]) / 2,
                    (geom.start[1] + geom.end[1]) / 2, 0
                );
                const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, mid);
                if (Math.abs(raycaster.ray.direction.dot(normal)) >= 0.02) {
                    hit = raycaster.ray.intersectPlane(plane, _hit);
                }
            }
            if (!hit) return;
            applyStation(resolveWallStation(geom, _hit.x, _hit.y));
        };

        const onMove = (ev: PointerEvent) => {
            if (dragRef.current) return;
            // Hover is an idle affordance: never re-raycast while a button
            // (box select, orbit) holds the pointer.
            if (ev.buttons !== 0) return;
            // Coarse movement threshold: sub-2px drift cannot change the hit.
            if (lastClient) {
                const dx = ev.clientX - lastClient.x;
                const dy = ev.clientY - lastClient.y;
                if (dx * dx + dy * dy < 4) return;
            }
            lastClient = { x: ev.clientX, y: ev.clientY };
            last = ev;
            if (raf == null) raf = requestAnimationFrame(apply);
        };

        window.addEventListener('pointermove', onMove, true);
        return () => {
            window.removeEventListener('pointermove', onMove, true);
            if (raf != null) cancelAnimationFrame(raf);
        };
    }, [active]);

    // Junction feedback: an end station whose endpoint is welded to another
    // wall moves the WHOLE junction — tint the control so the shared grab
    // is announced before the drag starts.
    const junctionShared = useMemo(() => {
        if (!displayWall || station === 'mid' || !g) return false;
        const pt = station === 'start' ? g.start : g.end;
        const elements = useBIMStore.getState().project.elements;
        for (const el of elements.values()) {
            if (el.category !== 'Walls' || el.id === displayWall.id) continue;
            const og = (el as WallElement).geometryData;
            if (!og) continue;
            if (Math.hypot(og.start[0] - pt[0], og.start[1] - pt[1]) < JUNCTION_WELD_TOL) return true;
            if (Math.hypot(og.end[0] - pt[0], og.end[1] - pt[1]) < JUNCTION_WELD_TOL) return true;
        }
        return false;
    }, [displayWall, station, g]);

    const handleDown = (e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        const wall = wallRef.current;
        const geom = wall?.geometryData;
        const kind = stationRef.current;
        if (!wall || !geom || dragRef.current) return;

        const dx = geom.end[0] - geom.start[0];
        const dy = geom.end[1] - geom.start[1];
        const runLen = Math.hypot(dx, dy);
        if (runLen < 1e-6) return;
        // Snap tolerance scales with camera distance (1.2%), shared with the
        // selection grips, so the snap feel stays constant across zoom.
        const wallMid = new THREE.Vector3(
            (geom.start[0] + geom.end[0]) / 2,
            (geom.start[1] + geom.end[1]) / 2,
            geom.baseOffset || 0
        );
        const snapTol = dragSnapTolerance(camera, wallMid);
        // The axis frame is the run's own line, anchored at the FIXED end and
        // directed TOWARD the dragged end, so a projection t is always the
        // positive run length (mid-slide offset anchor). End drags resolve
        // through the shared snap pipeline instead (free + locks).
        const origin = kind === 'start' ? { x: geom.end[0], y: geom.end[1] } : { x: geom.start[0], y: geom.start[1] };
        const dir = kind === 'start'
            ? { x: -dx / runLen, y: -dy / runLen }
            : { x: dx / runLen, y: dy / runLen };

        // Pointer projection at drag start (ground plane; shared by the
        // whole drag so the slide delta stays anchored).
        const groundPoint = (() => {
            const rect = gl.domElement.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return null;
            _ndc.set(
                ((e.nativeEvent.clientX - rect.left) / rect.width) * 2 - 1,
                -((e.nativeEvent.clientY - rect.top) / rect.height) * 2 + 1
            );
            camera.updateMatrixWorld();
            raycaster.setFromCamera(_ndc, camera);
            return raycaster.ray.intersectPlane(GROUND_PLANE, _hit)
                ? { x: _hit.x, y: _hit.y }
                : null;
        })();
        if (!groundPoint) return;

        const t0 = (groundPoint.x - origin.x) * dir.x + (groundPoint.y - origin.y) * dir.y;
        const origGeom = structuredClone(geom);
        dragRef.current = {
            kind,
            wallId: wall.id,
            origGeom,
            origin,
            dir,
            t0,
            originEndpoint: kind === 'start'
                ? [geom.start[0], geom.start[1]]
                : [geom.end[0], geom.end[1]],
            raf: null,
            lastEvent: null,
        };
        setDragWallId(wall.id);

        // Own-axis guide for the whole drag: the run's line extended past
        // both ends. Blue while the drag is free (re-angling), orange while
        // the endpoint locks onto the axis or a junction endpoint.
        const ext = Math.max(1.0, runLen * 0.6);
        const gz = (geom.baseOffset || 0) + 0.01;
        setAxisGuide({
            points: [
                [origin.x - dir.x * ext, origin.y - dir.y * ext, gz],
                [origin.x + dir.x * runLen + dir.x * ext, origin.y + dir.y * runLen + dir.y * ext, gz],
            ],
            snapped: false,
        });

        /** Pointer -> drag target (mm-rounded, shared by preview and commit
         *  so the committed geometry equals the last frame). */
        const computeTarget = (ev: PointerEvent): HoverDragTarget | null => {
            const rect = gl.domElement.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return null;
            _ndc.set(
                ((ev.clientX - rect.left) / rect.width) * 2 - 1,
                -((ev.clientY - rect.top) / rect.height) * 2 + 1
            );
            camera.updateMatrixWorld();
            raycaster.setFromCamera(_ndc, camera);
            const groundHit = raycaster.ray.intersectPlane(GROUND_PLANE, _hit);
            if (!groundHit) return null;
            if (kind === 'mid') {
                const t = (_hit.x - origin.x) * dir.x + (_hit.y - origin.y) * dir.y;
                const d = Math.round((t - t0) * 1000) / 1000;
                return { slide: { x: dir.x * d, y: dir.y * d } };
            }
            // End drag: full drafting snap pipeline + junction OSNAP + own-
            // axis lock; free 2D (re-angle) off the locks. Same resolution
            // the selection grips run.
            const res = resolveEndpointDragTarget(
                _hit,
                origGeom,
                kind === 'start' ? 'start' : 'end',
                camera,
                gl,
                snapTol,
                wall.id
            );
            return { endpoint: res.endpoint, locked: res.axisSnapped || res.endpointSnapped };
        };

        const applyFrame = (ev: PointerEvent) => {
            const drag = dragRef.current;
            if (!drag) return;
            const target = computeTarget(ev);
            if (!target) return;
            const nextGeometry = target.endpoint
                ? buildPreviewGeometry(drag.origGeom, drag.kind === 'start' ? 'start' : 'end', { endpoint: target.endpoint })
                : buildSlideGeometry(drag.origGeom, target.slide!);
            useWallEditPreviewStore.getState().set(drag.wallId, nextGeometry);
            // Guide feedback follows the same target: orange while locked.
            const locked = drag.kind === 'mid' || target.locked === true;
            setAxisGuide((prev) => (prev && prev.snapped !== locked ? { ...prev, snapped: locked } : prev));
            invalidate();
        };

        const onMove = (ev: PointerEvent) => {
            const drag = dragRef.current;
            if (!drag) return;
            drag.lastEvent = ev;
            if (drag.raf == null) {
                // Coalesce the pointer-move storm: at most one geometry
                // rebuild + preview write per animation frame.
                drag.raf = requestAnimationFrame(() => {
                    const d = dragRef.current;
                    if (d) d.raf = null;
                    if (d?.lastEvent) applyFrame(d.lastEvent);
                });
            }
            invalidate();
        };

        const removeListeners = () => {
            window.removeEventListener('pointermove', onMove, true);
            window.removeEventListener('pointerup', onUp, true);
            window.removeEventListener('pointercancel', onCancel, true);
        };

        const teardown = () => {
            const drag = dragRef.current;
            dragRef.current = null;
            setDragWallId(null);
            if (drag?.raf != null) cancelAnimationFrame(drag.raf);
            useWallEditPreviewStore.getState().clear();
            setAxisGuide(null);
            removeListeners();
            return drag;
        };

        const onCancel = () => { teardown(); };

        const onUp = (ev: PointerEvent) => {
            const drag = teardown();
            if (!drag) return;
            const target = computeTarget(ev);
            if (!target) return;
            const bim = useBIMStore.getState();
            if (!bim.project.elements.has(drag.wallId)) return;

            const finalGeometry = target.endpoint
                ? buildPreviewGeometry(drag.origGeom, drag.kind === 'start' ? 'start' : 'end', { endpoint: target.endpoint })
                : buildSlideGeometry(drag.origGeom, target.slide!);
            bim.updateElement(drag.wallId, { geometryData: finalGeometry } as any);

            if (drag.kind === 'mid') {
                // A slide moves the run within the existing joins: re-solve
                // the dragged wall's profiles, neighbors stay put.
                recomputeWallJoins(drag.wallId);
            } else {
                // End drag at a shared junction: every object welded to the
                // endpoint follows the same delta, so two chain walls extend
                // together instead of tearing apart. The dragged wall is
                // excluded (it just committed its own geometry).
                const oldEnd = drag.kind === 'start' ? drag.origGeom.start : drag.origGeom.end;
                const newEnd = drag.kind === 'start' ? finalGeometry.start : finalGeometry.end;
                const delta = new THREE.Vector3(
                    newEnd[0] - oldEnd[0],
                    newEnd[1] - oldEnd[1],
                    (newEnd[2] ?? 0) - (oldEnd[2] ?? 0)
                );
                if (delta.lengthSq() > 1e-12) {
                    const carried = applyJunctionStretch(
                        new THREE.Vector3(oldEnd[0], oldEnd[1], oldEnd[2] ?? 0),
                        delta,
                        'Drag Wall Endpoint',
                        { excludeId: drag.wallId }
                    );
                    if (carried > 0) {
                        console.log('Wall hover control:', carried, 'joined object(s) carried with the junction');
                    }
                }
                recomputeWallJoins(drag.wallId);
            }
            invalidate();
        };

        window.addEventListener('pointermove', onMove, true);
        window.addEventListener('pointerup', onUp, true);
        window.addEventListener('pointercancel', onCancel, true);
    };

    if (!enabled || !displayWall || !g) return null;

    // Control position: the station point on the live (preview) geometry.
    const height = g.height ?? 3;
    const z = viewType === 'plan' ? RING_Z : (g.baseOffset || 0) + height / 2;
    const position: [number, number, number] = station === 'start'
        ? [g.start[0], g.start[1], z]
        : station === 'end'
            ? [g.end[0], g.end[1], z]
            : [(g.start[0] + g.end[0]) / 2, (g.start[1] + g.end[1]) / 2, z];
    const tint = junctionShared ? SNAP_COLOR : HANDLE_COLOR;

    return (
        <group>
            <group position={position}>
                {/* Invisible enlarged hit box keeps the small control
                    grabbable; isWallHandle keeps it out of every pick. */}
                <mesh
                    onPointerDown={handleDown}
                    onPointerMove={(e) => e.stopPropagation()}
                    userData={{ isWallHandle: true }}
                >
                    <boxGeometry args={[HANDLE_SIZE * 3, HANDLE_SIZE * 3, HANDLE_SIZE * 3]} />
                    <meshBasicMaterial transparent opacity={0} depthWrite={false} />
                </mesh>
                {viewType === 'plan' ? (
                    // Flat ring on the location line — drawn over the plan
                    // cut geometry (depth test off, transparent pass).
                    <mesh renderOrder={10} userData={{ isWallHandle: true }}>
                        <torusGeometry args={[RING_RADIUS, RING_TUBE, 8, 24]} />
                        <meshBasicMaterial color={tint} transparent opacity={0.95} depthTest={false} />
                    </mesh>
                ) : (
                    <mesh userData={{ isWallHandle: true }}>
                        <sphereGeometry args={[HANDLE_SIZE, 12, 12]} />
                        <meshBasicMaterial color={tint} />
                    </mesh>
                )}
            </group>
            {axisGuide && <WallAxisGuide guide={axisGuide} />}
        </group>
    );
}
